// @flow

import React from 'react'
import styled from 'styled-components'
import {ChevronDown} from 'react-feather'
import {formatMessage} from '../../i18n'

import Text from './Text'

const SelectWrapper = styled.div`
  position: relative;
`

const SelectLabel = styled(Text).attrs({type: 'secondary'})`
  margin-left: 8px;
`

const SelectIcon = styled(ChevronDown)`
  position: absolute;
  bottom: 12px;
  right: 16px;
  pointer-events: none;
`

const SelectTriggerWrapper = styled.div`
  position: relative;
  margin: 8px 0 ${props => (props.withoutSpacing ? '0' : '24px')} 0;
`

const SelectTrigger = styled.button`
  display: block;
  border: 1px solid #d8e1e6;
  width: 100%;
  background-color: #fff;
  padding: 0 40px 0 16px;
  height: 50px;
  font-size: 16px;
  font-family: 'basier_circle', sans-serif;
  cursor: pointer;
  text-align: left;
  outline: none;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${props =>
    props.isEmpty
      ? props.theme.inputfield.colors.input.placeholder
      : props.theme.inputfield.colors.input.color};
`

const SelectPanel = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #d8e1e6;
  z-index: 200;
  max-height: 300px;
  overflow-y: auto;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
`

const SelectOptionRow = styled.div`
  padding: 10px 16px;
  font-size: 16px;
  font-family: 'basier_circle', sans-serif;
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  color: ${props =>
    props.disabled
      ? props.theme.inputfield.colors.input.placeholder
      : props.theme.inputfield.colors.input.color};
  background-color: ${props => (props.isSelected ? '#f0f4f6' : 'transparent')};

  &:hover {
    background-color: ${props => (props.disabled ? 'transparent' : '#f5f5f5')};
  }
`

type Props = {
  label?: string,
  placeholder?: string,
  value: string,
  options: Array<{value: string, label: string, disabled?: boolean}>,
  withoutSpacing?: boolean,
  required?: boolean,
  onChange: (e: {target: {value: string}}) => void
}

const Select = ({
  label,
  placeholder,
  value,
  options,
  withoutSpacing,
  required,
  onChange,
  ...restProps
}: Props) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const wrapperRef = React.useRef(null)

  React.useEffect(() => {
    const handleClickOutside = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Coerce to string for comparison — mirrors native <select> which always
  // converts option values to strings (prevents boolean `false` collapsing via `|| ''`).
  const stringValue = value !== undefined && value !== null ? String(value) : ''
  const selectedOption = options.find(o => String(o.value) === stringValue)
  const displayText = selectedOption ? selectedOption.label : null

  const handleSelect = optionValue => {
    onChange({target: {value: String(optionValue)}})
    setIsOpen(false)
  }

  return (
    <SelectWrapper ref={wrapperRef} {...restProps}>
      {label && (
        <SelectLabel>
          {label}
          {required && (
            <Text type="secondary">{` (${formatMessage('mandatory')})`}</Text>
          )}
        </SelectLabel>
      )}
      <SelectTriggerWrapper withoutSpacing={withoutSpacing}>
        <SelectTrigger
          type="button"
          isEmpty={!displayText}
          onClick={() => setIsOpen(v => !v)}
        >
          {displayText || placeholder}
        </SelectTrigger>
        <SelectIcon />
        {isOpen && (
          <SelectPanel>
            {options.map(option => (
              <SelectOptionRow
                key={option.value}
                data-value={String(option.value)}
                disabled={option.disabled}
                isSelected={String(option.value) === stringValue}
                onClick={() => !option.disabled && handleSelect(option.value)}
              >
                {option.label}
              </SelectOptionRow>
            ))}
          </SelectPanel>
        )}
      </SelectTriggerWrapper>
    </SelectWrapper>
  )
}

export default Select
